import React, { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import styled from 'styled-components';
import {
  Button,
  Typography,
  Box,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  Link,
  Avatar,
  Badge,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import PricingSection from '../../components/PricingSection';
import { selectAuth } from '../../redux/slices/auth/authSlice';
import { useExpert } from '../../redux/slices/expert/useExpert';

const PageContainer = styled.div`
  min-height: 100vh;
  background: white;
  overflow-x: hidden;
`;

const NavBar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 40px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #333;
`;

const NavLinks = styled.div<{ isMobile?: boolean }>`
  display: flex;
  gap: 32px;
  align-items: center;

  @media (max-width: 768px) {
    ${props => props.isMobile ? `
      flex-direction: column;
      gap: 24px;
      padding: 32px;
      width: 100%;
    ` : `
      display: none;
    `}
  }
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 60px 20px;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 40px 16px;

    h1 {
      font-size: 2rem !important;
    }

    h5 {
      font-size: 1.2rem !important;
    }
  }
`;

const DomainCheckSection = styled.div`
  background: #f7f7f7;
  display: flex;
  justify-content: center;
`

const MaxLimitedContainer = styled.div`
  text-align: center;
  padding: 40px 0;
  max-width: 800px;
  margin: 0 auto;

`

const ProcessSection = styled.div`
  padding: 50px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const FeatureSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
`

const FeatureItem = styled.button`
  display: flex;
  flex-direction: column;
  padding: 20px;
  justify-content: center;
  align-items: center;
  text-align: center;

  & img {
    margin: 30px;
    max-width: 400px;
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  }
`

const DomainInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 20px 0;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin: 24px 0;
  }

  input {
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: 300px;
    font-size: 16px;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 300px;
    }
  }
`;

const FeaturesSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  margin: 20px 0;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 16px;
    flex-direction: column;
    align-items: center;
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
`;

const FooterSection = styled.div`
  background: #1e2532;
  color: white;
  padding: 60px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 40px 16px;

    h2 {
      font-size: 2rem !important;
    }
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const MenuButton = styled(IconButton)`
  display: none !important;

  @media (max-width: 768px) {
    display: flex !important;
  }
`;

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const LandingPage = () => {
  const { isLoggedIn } = useSelector(selectAuth);
  const { body: expertProfile } = useExpert()

  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNavigateToLogin = () => {
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const handleNavigateToExpertProfilePage = () => {
    if (isLoggedIn) {
      navigate('/expert/profile');
      setMobileMenuOpen(false);
    }
  };

  const handleNavigateToLeadsPage = () => {
    if (isLoggedIn) {
      navigate('/expert/collect');
    } else {
      navigate('/login')
    }
  }

  const handleNavigateToBookingPage = () => {
    navigate('/' + expertProfile?.preferred_link)
  }

  const handleCheckAvailability = () => {
    navigate('/login');
  };

  const handleSubscribe = () => {
    navigate('/expert/billing');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const subDomainFeatures = [
    'Branded triplink.vip sub-domain',
    'Custom lading page colours',
    'Team members & management',
  ];

  const processFeatures = [
    { name: 'Customers', url: '/assets/images/Customer_Hammock_Graphic.svg', description: 'Enquire & Book Meetings', action: handleNavigateToBookingPage },
    { name: 'Travel Agents', url: '/assets/images/Travel_Agent_Graphic_reverse.svg', description: 'Manage Leads & Calendar', action: handleNavigateToExpertProfilePage },
  ];

  return (
    <PageContainer>
      <NavBar>
        <Logo>triplink.vip</Logo>
        {isLoggedIn ? (
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
          // sx={{
          //   display: "none", // Default: Hidden
          //   "@media (min-width: 768px)": {
          //     display: "block", // Show only when width is >= 768px
          //   },
          // }}
          >
            <Avatar src={expertProfile?.profile_image_url} onClick={handleNavigateToLeadsPage} sx={{ cursor: 'pointer' }} />
          </StyledBadge>
        ) : (
          <NavLinks>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNavigateToLogin}
            >
              Login
            </Button>
          </NavLinks>
        )}
        {!isLoggedIn && <MenuButton
          edge="end"
          color="inherit"
          aria-label="menu"
          onClick={toggleMobileMenu}
        >
          <MenuIcon />
        </MenuButton>}
      </NavBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: '300px',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={toggleMobileMenu}>
            <CloseIcon />
          </IconButton>
        </Box>
        <NavLinks isMobile>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNavigateToLogin}
            fullWidth
          >
            Login
          </Button>
        </NavLinks>
      </Drawer>

      <HeroSection>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          triplink.vip
        </Typography>
        <Typography variant="h5" color="textSecondary" gutterBottom>
          Automate collection of trip requirements and meeting scheduling for your travel business.
        </Typography>
      </HeroSection>


      <ProcessSection>
        <Typography variant="h5" sx={{ fontWeight: 600, padding: "20px" }}>See How It Works</Typography>
        <FeatureSection>
          {processFeatures.map((i) => (
            <FeatureItem onClick={i.action} key={i.name}>
              <img src={i.url} alt={i.name} />
              <Typography variant="h6" sx={{ fontWeight: 600 }} textAlign="center">{i.name}</Typography>
              <Typography color="textSecondary" textAlign="center">{i.description}</Typography>
            </FeatureItem>
          ))}
        </FeatureSection>
      </ProcessSection>

      <DomainCheckSection>
        <MaxLimitedContainer>
          <Typography variant="h5" sx={{ fontWeight: 600, padding: '30px' }}>
            Custom triplink.vip Domain
          </Typography>
          <Typography color="textSecondary">
            If you operate a travel business and want to give customers the <Typography component="span" fontWeight={600}>VIP treatment while also saving time and money,</Typography> <Link component="button">sign up for a Business Plan</Link> and also get:
          </Typography>
          <List>
            {subDomainFeatures.map(i => (
              <ListItem sx={{ justifyContent: 'center' }} key={i}>{`● ${i}`}</ListItem>
            ))}
          </List>
          <Typography>
            Check if your brands custom link is available then <Link component="button">try 30 days free</Link>.
          </Typography>
          <DomainInput>
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
              http://triplink.vip/
            </Typography>
            <input placeholder="Enter Your Details" />
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleCheckAvailability}
              sx={{
                backgroundColor: '#00bfa5',
                width: isMobile ? '100%' : 'auto',
                maxWidth: '300px'
              }}
            >
              Check Availability
            </Button>
          </DomainInput>
        </MaxLimitedContainer>
      </DomainCheckSection>

      <FeaturesSection>
        <Feature>
          <CheckCircleIcon color="error" />
          <span>Free 30-day trial</span>
        </Feature>
        <Feature>
          <CheckCircleIcon color="error" />
          <span>Use Anywhere Digitally</span>
        </Feature>
        <Feature>
          <CheckCircleIcon color="error" />
          <span>Save time</span>
        </Feature>
      </FeaturesSection>

      <PricingSection
        onSelectPrices={handleSubscribe}
        shouldTrial={true}
        skipQuantity={true}
        defaultInterval={'month'}
        defaultQuantity={1}
        canChangeInterval={true}
        enabledProducts={['free', 'prod_tl_individual-2025-wGCk4x2O6p3j', 'prod_tl_business-2025-N9kH4gDqXydb']}
      />

      <FooterSection>
        <Link href="https://lyfshort.com/support/" target="_blank" variant="h4" underline="none" color="white">
          Contact Us
        </Link>
        <FooterContent>
          <Box height={100} />
          <Typography textAlign="start">Copyright Lyfshort Pty. Ltd.</Typography>
        </FooterContent>
      </FooterSection>
    </PageContainer>
  );
};

export default LandingPage;
